<template>
  <div class="page-container api-keys-page">
    <portal to="page-name">Chaves de API</portal>
    <div class="card card-page">
      <div class="card-header">
        <h1 class="card-title">Chaves de API</h1>
      </div>
      <div class="card-body">
        <template v-if="!canAccess">
          <div class="empty mt-2">
            <div class="empty-icon">
              <fa-icon :icon="['fal', 'lock']" size="3x"/>
            </div>
            <p class="empty-title h5">Acesso Negado</p>
            <p class="empty-subtitle">
              Você não possui permissão para gerenciar chaves de API.
            </p>
          </div>
        </template>
        <template v-else>
          <div class="columns">
            <div class="column">
              <p class="text-gray mb-2">
                As chaves de API permitem acesso programático à sua conta.
                Mantenha suas chaves seguras e nunca as compartilhe publicamente.
              </p>
            </div>
            <div class="column col-auto">
              <button class="btn btn-primary btn-icon btn-icon-left"
                      @click="showCreateModal = true">
                <fa-icon :icon="['fal', 'plus']"/>Gerar Nova Chave
              </button>
            </div>
          </div>

          <div class="loading loading-lg mt-2" v-if="loading"></div>

          <template v-else>
            <template v-if="data.items.length === 0">
              <div class="empty mt-2">
                <div class="empty-icon">
                  <fa-icon :icon="['fal', 'key']" size="3x" />
                </div>
                <p class="empty-title h5">Nenhuma chave de API</p>
                <p class="empty-subtitle">
                  Você ainda não possui chaves de API. Clique em "Gerar Nova Chave"
                  para criar sua primeira chave.
                </p>
              </div>
            </template>
            <template v-else>
              <table class="table table-striped table-hover mt-2">
                <thead>
                  <tr>
                    <th>Chave</th>
                    <th class="hide-sm">Throttling</th>
                    <th class="hide-sm hide-md">Rotas</th>
                    <th class="hide-sm">Data de Criação</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, i) in data.items" :key="i">
                    <td>
                      <code>{{ getMaskedKey(item.apiKey) }}</code>
                    </td>
                    <td class="hide-sm">
                      <span class="text-small text-gray">
                        {{ item.throttling.requestsNumber }} req /
                        {{ formatThrottling(item.throttling.milliseconds) }}
                      </span>
                    </td>
                    <td class="hide-sm hide-md">
                      <span class="text-small text-gray">
                        {{ item.routes.length }} rota(s)
                      </span>
                    </td>
                    <td class="hide-sm">{{ formatDate(item.createdAt) }}</td>
                    <td class="text-right">
                      <button class="btn btn-sm btn-action btn-icon btn-error tooltip"
                              @click="confirmDelete(item)" data-tooltip="Remover chave">
                        <fa-icon :icon="['fal', 'trash']"></fa-icon>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </template>
          </template>
        </template>
      </div>
      <div class="card-footer" v-if="canAccess && data.items.length > 0">
        <span>Total de chaves:
          <span class="ml-2 text-primary text-bold">{{ data.total }}</span>
        </span>
      </div>
    </div>

    <create-key-modal
      :show="showCreateModal"
      @close="showCreateModal = false"
      @created="handleKeyCreated"
    />

    <delete-key-modal
      :show="showDeleteModal"
      :api-key="selectedKey"
      @close="showDeleteModal = false"
      @deleted="handleKeyDeleted"
    />
  </div>
</template>

<script>
import moment from 'moment';
import { MANAGE_CUSTOMER_ACCOUNT } from '@/data/actions/modules/general';
import CreateKeyModal from './CreateKeyModal.vue';
import DeleteKeyModal from './DeleteKeyModal.vue';

export default {
  components: {
    CreateKeyModal,
    DeleteKeyModal,
  },
  data() {
    return {
      loading: false,
      showCreateModal: false,
      showDeleteModal: false,
      selectedKey: null,
      visibleKeys: {},
      data: {
        items: [],
        total: 0,
      },
    };
  },
  computed: {
    canAccess() {
      return this.$can(MANAGE_CUSTOMER_ACCOUNT);
    },
  },
  mounted() {
    if (this.canAccess) {
      this.load();
    }
  },
  methods: {
    load() {
      this.loading = true;

      this.$http.get('/gateway/api-keys')
        .then(({ data }) => {
          this.data.items = data.items || data;
          this.data.total = data.total || data.length;
        })
        .catch((error) => {
          this.$toast.error(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    confirmDelete(key) {
      this.selectedKey = key;
      this.showDeleteModal = true;
    },
    handleKeyCreated(data) {
      this.data.items.push(data);
      this.data.total += 1;
    },
    handleKeyDeleted() {
      this.data.items.splice(this.data.items.indexOf(this.selectedKey), 1);
      this.data.total -= 1;
      this.selectedKey = null;
      this.showDeleteModal = false;
    },
    formatDate(date) {
      return moment(date).format('DD/MM/YYYY HH:mm');
    },
    getMaskedKey(apiKey) {
      if (!apiKey) return '****';
      const keyLength = apiKey.length;
      const visibleChars = 8;
      if (keyLength <= visibleChars) return apiKey;
      return `${'*'.repeat(keyLength - visibleChars)}${apiKey.slice(-visibleChars)}`;
    },
    formatThrottling(milliseconds) {
      if (milliseconds < 1000) return `${milliseconds}ms`;
      const seconds = milliseconds / 1000;
      if (seconds < 60) return `${seconds}s`;
      const minutes = Math.floor(seconds / 60);
      return `${minutes}min`;
    },
  },
};
</script>

<style lang="scss">
  @import "./src/assets/scss/_variables.scss";

  .api-keys-page {
    code {
      font-family: 'Courier New', monospace;
      background-color: $bg-color-dark;
      padding: 0.2rem 0.4rem;
      border-radius: $border-radius;
    }
    .actions {
      display: flex;
      gap: 4px;
    }
  }
</style>
